import { ReactNode } from "react";
import cn from "classnames";

interface DatePickerProps {
  label: string | ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  id?: string;
  value: string; // ISO date string
  onChange: (value: string) => void;
  className?: string;
}

export default function DatePicker({
  className,
  label,
  size = "md",
  id,
  value,
  onChange,
  ...props
}: DatePickerProps) {
  const uniqueId =
    id ||
    `datepicker-${
      typeof label === "string"
        ? label.toLowerCase().replace(/\s+/g, "-")
        : "custom"
    }`;

  function getDatePickerSizes() {
    switch (size) {
      case "xs":
        return {
          input: "text-xs px-1 max-w-24",
          label: "text-xs",
        };
      case "sm":
        return {
          input: "text-xs md:text-sm px-1 md:px-4 max-w-32 md:max-w-36",
          label: "text-xs md:text-base",
        };
      case "lg":
        return {
          input: "text-base px-6 max-w-fit",
          label: "text-lg",
        };
      case "md":
      default:
        return {
          input: "text-sm px-4 max-w-fit",
          label: "text-base",
        };
    }
  }

  const sizeClasses = getDatePickerSizes();

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <fieldset className="form-control w-full">
      <div className="label p-0 min-h-10">
        <span className="flex items-center">
          <span className={`text-gray-800 dark:text-base-content/80 font-medium ${sizeClasses.label}`}>
            {label}
          </span>
        </span>
        <input
          id={uniqueId}
          type="date"
          value={value}
          onChange={handleDateChange}
          className={cn(
            "input input-bordered cursor:pointer",
            "focus:outline-none focus:border-gray-300 dark:focus:border-base-300",
            "border-gray-300 dark:border-base-300 bg-base-100 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/40",
            "transition-colors",
            sizeClasses.input,
            "!min-h-10 h-[25px]",
            className
          )}
        />
      </div>
    </fieldset>
  );
}
